import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  BodyTitle,
  CardChildMarker,
  CardDisclosureChevron,
  CardDisclosurePanel,
  CardDisclosureToggle,
  CardFields,
  CardHeader,
  CardIdentity,
  CardStatus,
  CardTask,
  CardUtilities,
  CardsGrid,
  CardToolbarControls,
  RowWrapper,
  SelectAllBox,
  SelectAllButton,
  SelectionBar,
  SelectionBarLabel,
  SelectionClearButton,
  TableCard,
  TableControlsPanel,
  TableCustom,
  TableEmptyState,
  TableToolbar,
  TableWrapper,
  ToolbarMeta,
  ToolbarActions,
  ToolbarSearch,
  ToolbarSelect,
  ToolbarTitle,
  Tr,
  ViewButton,
  ViewSwitcher,
} from "./styles";
import { useTranslation } from "react-i18next";
import Select from "../select";
import Pages from "../pagination";
import TableColumnItem from "./column";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";
import TableRowItem from "./row";
import StyledIcon from "../styled-icon";
import { icons } from "../../icons";
import { filterPermissionAwareActions, usePermission } from "../../permissions";
import { useMediaQuery } from "../../hooks/useMediaQuery";

type TableView = "table" | "cards";

type TableProps = {
  columns: Column[];
  rows: any[];
  hideCheckbox?: boolean;
  hideOther?: boolean;
  isNotifications?: boolean;
  settings?: any[];
  limitedItems?: any[];
  paginationData?: any;
  pages?: any;
  updateData?: (params?: any) => void;
  trigger?: any;
  allChecked?: any;
  setAllChecked?: any;
  checked?: any[];
  setChecked?: any;
  loading?: boolean;
  hasDroppedRow?: boolean;
  withoutHeader?: boolean;
  customSetPage?: any;
  customSetPageSize?: any;
  customPageSize?: any;
  filterSettings?: (row: any, options: any[]) => any[];
  allowCardView?: boolean;
  defaultView?: TableView;
  searchable?: boolean;
  toolbarActions?: React.ReactNode;
  /** Deprecated: the selection bar now appears in card view whenever rows are selected. */
  cardSelectionToolbar?: boolean;
  hasActiveFilters?: boolean;
  /** Heading shown at the start of the toolbar; replaces the item counter. */
  title?: React.ReactNode;
  searchPlaceholder?: string;
  /** Bulk actions shown in the selection bar while rows are selected. */
  selectionActions?: React.ReactNode;
};

type Column = {
  title: string;
  dataIndex?: string;
  key: string;
  width?: number;
  hideCheckbox?: boolean;
  withBackground?: boolean;
  sorted?: boolean;
  orderingKey?: string;
  cardHidden?: boolean;
  cardFullWidth?: boolean;
};

const pageSizeOptions = [10, 20, 30, 50, 100].map((value) => ({ value, label: value }));
const primaryColumnKeys = new Set(["clear_name", "floating_ip_address", "direction", "name", "id", "port"]);
const fullWidthCardField = /(^|_)(ip|address|secret|token|key|roles?|description|subnets?|url)($|_)/i;

const hasCardValue = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  return !Array.isArray(value) || value.length > 0;
};

const rowKey = (row: any, index: number, prefix = "row") => {
  const identity = row?.id ?? (typeof row?.name === "string" || typeof row?.name === "number" ? row.name : null);
  return `${prefix}-${identity ?? index}`;
};

const Table: FC<TableProps> = (props) => {
  const [page, setPage] = useQueryParam("page", NumberParam);
  const [ordering, setOrdering] = useQueryParam("ordering", StringParam);
  const [pageSize, setPageSize] = useQueryParam("page_size", StringParam);
  const [search, setSearch] = useQueryParam("search", StringParam);
  const [hover, setHover] = useState<any>(null);
  const [dropNodes, setDropNodes] = useState<any>(false);
  const [openHistory, setOpenHistory] = useState<Set<string>>(() => new Set());
  const [showItems, setShowItems] = useState<any>(props.customPageSize || pageSize || 10);
  const [t] = useTranslation();
  const { snapshot } = usePermission();
  const isMobile = useMediaQuery(
    "(max-width: 768px)",
    typeof window !== "undefined" && window.innerWidth <= 768,
  );

  const {
    columns,
    rows,
    hideOther,
    hideCheckbox,
    isNotifications,
    settings,
    limitedItems,
    paginationData,
    updateData,
    allChecked,
    setAllChecked,
    checked,
    setChecked,
    loading,
    hasDroppedRow,
    withoutHeader,
    customSetPage,
    customSetPageSize,
    customPageSize,
    filterSettings,
    allowCardView,
    defaultView = "table",
    searchable,
    toolbarActions,
    hasActiveFilters,
    title,
    searchPlaceholder,
    selectionActions,
  } = props;
  const [searchValue, setSearchValue] = useState(search || "");
  const setCheckedRef = useRef(setChecked);
  const updateDataRef = useRef(updateData);
  const previousUpdateParams = useRef({ page, ordering, pageSize: showItems, search });

  const desktopCardViewEnabled = Boolean(allowCardView && !hideOther);
  const [view, setView] = useState<TableView>(() => {
    if (typeof window === "undefined") {
      return defaultView;
    }

    const savedView = window.localStorage.getItem("table_view_ocp");
    return savedView === "cards" || savedView === "table" ? savedView : defaultView;
  });
  const isCardView = isMobile || (desktopCardViewEnabled && view === "cards");

  setCheckedRef.current = setChecked;
  updateDataRef.current = updateData;

  useEffect(() => {
    setShowItems(customPageSize || pageSize || 10);
  }, [customPageSize, pageSize]);

  useEffect(() => {
    setSearchValue(search || "");
  }, [search]);

  useEffect(() => {
    if (!searchable || searchValue === (search || "")) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setSearch(searchValue.trim() || undefined);
      setPage(1);
    }, 400);

    return () => window.clearTimeout(timeout);
  }, [search, searchValue, searchable, setPage, setSearch]);

  useEffect(() => {
    const previous = previousUpdateParams.current;
    if (previous.page === page && previous.ordering === ordering && previous.pageSize === showItems && previous.search === search) {
      return;
    }
    previousUpdateParams.current = { page, ordering, pageSize: showItems, search };

    if (!updateDataRef.current) {
      return;
    }

    const descending = ordering?.startsWith("-") ?? false;
    const orderingKey = descending ? ordering?.slice(1) : ordering;
    updateDataRef.current({
      ordering,
      search,
      page_size: showItems,
      page: page || 1,
      sort: orderingKey ? [orderingKey, !descending] : [],
      pageSize: showItems,
    });
  }, [ordering, page, search, showItems]);

  const getVisibleRowActions = useCallback((row: any) => {
    const baseOptions = row?.dropOptions?.length > 0 ? row.dropOptions : settings || [];
    const rowOptions = filterSettings ? filterSettings(row, baseOptions) : baseOptions;
    const rowLimitedItems = filterSettings ? filterSettings(row, limitedItems || []) : limitedItems || [];
    const filterInput = {
      itemAllowedActions: row?.allowed_actions,
      extraGuards: {
        invitationAccepted: !row?.invitation,
        invitationPending: Boolean(row?.invitation),
      },
    };
    const visibleOptions = filterPermissionAwareActions(snapshot, rowOptions || [], filterInput);
    const visibleLimitedItems = filterPermissionAwareActions(snapshot, rowLimitedItems || [], filterInput);

    return row?.invitation ? visibleLimitedItems : visibleOptions;
  }, [filterSettings, limitedItems, settings, snapshot]);

  const resolvedColumns = useMemo(() => {
    const hasPropsColumn = columns.some((column) => column.key === "props");

    if (!hasPropsColumn) {
      return columns;
    }

    const hasVisibleAction = rows.some((row) => {
      if (hasCardValue(row?.props)) {
        return true;
      }

      return !row?.hideProps && getVisibleRowActions(row).length > 0;
    });

    return hasVisibleAction ? columns : columns.filter((column) => column.key !== "props");
  }, [columns, getVisibleRowActions, rows]);

  const primaryColumn = useMemo(
    () => resolvedColumns.find((column) => primaryColumnKeys.has(column.key)) || resolvedColumns.find((column) => column.key !== "props" && column.key !== "eye"),
    [resolvedColumns],
  );
  const actionColumn = useMemo(
    () => resolvedColumns.find((column) => column.key === "props" || column.key === "eye"),
    [resolvedColumns],
  );
  const sortableColumns = useMemo(() => resolvedColumns.filter((column) => column.sorted), [resolvedColumns]);
  const sortOptions = useMemo(
    () => [
      { value: "", label: t("DEFAULT_SORT") },
      ...sortableColumns.flatMap((column) => [
        { value: column.orderingKey || column.key, label: `${column.title} A-Z` },
        { value: `-${column.orderingKey || column.key}`, label: `${column.title} Z-A` },
      ]),
    ],
    [sortableColumns, t],
  );

  const canSelectAll = !hideCheckbox && typeof setChecked === "function" && typeof setAllChecked === "function";
  const selectedCount = Array.isArray(checked) ? checked.length : 0;
  const totalCount = paginationData?.count ?? rows.length;

  const toggleAll = () => {
    if (!canSelectAll) {
      return;
    }

    if (allChecked) {
      if (setChecked.length > 1) {
        setChecked(null);
      } else {
        (checked || []).forEach((row) => {
          flushSync(() => setCheckedRef.current?.(row));
        });
      }
      setAllChecked(false);
      return;
    }

    const selectedIds = new Set((checked || []).map((row) => row?.id));
    rows
      .filter((row) => !row?.router_external && !row?.hideCheckbox && !selectedIds.has(row?.id))
      .forEach((row) => {
        if (setChecked.length > 1) {
          setChecked(row, true);
        } else {
          flushSync(() => setCheckedRef.current?.(row));
        }
      });
    setAllChecked(true);
  };

  const clearSelection = () => {
    if (typeof setChecked !== "function") {
      return;
    }

    if (setChecked.length > 1) {
      setChecked(null);
    } else {
      (checked || []).forEach((row) => {
        flushSync(() => setCheckedRef.current?.(row));
      });
    }
    setAllChecked?.(false);
  };

  const toggleHistory = (key: string) => {
    setOpenHistory((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const changeView = (nextView: TableView) => {
    setView(nextView);
    window.localStorage.setItem("table_view_ocp", nextView);
  };

  const commonRowProps = {
    hasDroppedRow,
    dropNodes,
    setDropNodes,
    setHover,
    hover,
    hideCheckbox,
    isNotifications,
    settings,
    limitedItems,
    allChecked,
    checked,
    setChecked,
    filterSettings,
  };

  const renderTableRow = (item: any, index: number, disableSettings = false) => (
    <Tr key={rowKey(item, index, disableSettings ? "child" : "row")}>
      {resolvedColumns.map((column) => (
        <PreRowItem
          {...commonRowProps}
          key={`${rowKey(item, index)}-${column.key}`}
          item={item}
          index={index}
          column={column}
          first={column.key === primaryColumn?.key}
          disableSettings={disableSettings}
        />
      ))}
    </Tr>
  );

  const renderCard = (item: any, index: number, disableSettings = false) => {
    const promotedStatusColumn = resolvedColumns.find((column) =>
      column.key !== primaryColumn?.key &&
      column.key !== actionColumn?.key &&
      (/(^|_)(status|state)($|_)/i.test(column.key) || /status|state/i.test(column.title)) &&
      hasCardValue(item[column.key]),
    );
    const fields = resolvedColumns.filter((column) =>
      column.key !== primaryColumn?.key &&
      column.key !== actionColumn?.key &&
      column.key !== promotedStatusColumn?.key &&
      !column.cardHidden &&
      hasCardValue(item[column.key]),
    );
    const isSelected = Boolean(item?.id !== undefined && checked?.some((selected) => selected?.id === item.id));
    const directAction = actionColumn ? item[actionColumn.key] : null;
    const hasDirectAction = Boolean(actionColumn?.key === "props" && hasCardValue(directAction));
    const hasHeaderAction = Boolean(
      actionColumn &&
      !disableSettings &&
      (actionColumn.key === "eye" ? directAction || item?.onPressEye : !hasDirectAction && !item?.hideProps && getVisibleRowActions(item).length > 0),
    );
    const cardLabel = typeof item?.name === "string" || typeof item?.id === "string" || typeof item?.id === "number"
      ? String(item.name ?? item.id)
      : undefined;
    const cardKey = rowKey(item, index, disableSettings ? "child-card" : "card");
    const hasHistoryDisclosure = Boolean(hasDroppedRow && item?.hasHistory && !disableSettings);
    const historyOpen = hasHistoryDisclosure && openHistory.has(cardKey);
    const hasNodesDisclosure = Boolean(item?.customBodyRow && !disableSettings);
    const nodesOpen = hasNodesDisclosure && dropNodes === item?.id;

    return (
      <TableCard
        key={cardKey}
        aria-label={cardLabel}
        $selected={isSelected}
        $hasDisplayTask={item?.display_task !== null && item?.display_task !== undefined}
        $failed={Boolean(item?.cardFailed)}
        $child={disableSettings}
      >
        <CardHeader>
          {disableSettings ? <CardChildMarker aria-hidden="true">└</CardChildMarker> : null}
          <CardIdentity>
            {primaryColumn ?
              <PreRowItem
                {...commonRowProps}
                item={item}
                index={index}
                column={primaryColumn}
                first
                layout="card"
                cardRole="primary"
                disableSettings={disableSettings}
              />
            : null}
            {promotedStatusColumn ?
              <CardStatus $indented={!hideCheckbox && !item?.hideCheckbox}>
                <PreRowItem
                  {...commonRowProps}
                  item={item}
                  index={index}
                  column={promotedStatusColumn}
                  layout="card"
                  cardRole="status"
                  disableSettings={disableSettings}
                />
              </CardStatus>
            : null}
          </CardIdentity>
          {hasHeaderAction && actionColumn ?
            <PreRowItem
              {...commonRowProps}
              item={item}
              index={index}
              column={actionColumn}
              layout="card"
              cardRole="action"
              disableSettings={disableSettings}
            />
          : null}
        </CardHeader>
        {item?.display_task ?
          <CardTask>
            <span aria-hidden="true"><StyledIcon src={icons.Warning} /></span>
            <span>{item.display_task}</span>
          </CardTask>
        : null}
        {fields.length > 0 ?
          <CardFields>
            {fields.map((column) => (
              <PreRowItem
                {...commonRowProps}
                key={`${rowKey(item, index)}-card-${column.key}`}
                item={item}
                index={index}
                column={column}
                layout="card"
                cardRole="field"
                fullWidth={column.cardFullWidth || fullWidthCardField.test(column.key)}
                disableSettings={disableSettings}
              />
            ))}
          </CardFields>
        : null}
        {hasDirectAction && actionColumn && !disableSettings ?
          <CardUtilities>
            <PreRowItem
              {...commonRowProps}
              item={item}
              index={index}
              column={actionColumn}
              layout="card"
              cardRole="utility"
              disableSettings={disableSettings}
            />
          </CardUtilities>
        : null}
        {historyOpen ? <CardDisclosurePanel>{item.customBody}</CardDisclosurePanel> : null}
        {hasHistoryDisclosure || hasNodesDisclosure ?
          <CardDisclosureToggle
            type="button"
            aria-expanded={hasHistoryDisclosure ? historyOpen : nodesOpen}
            onClick={() => {
              if (hasHistoryDisclosure) {
                toggleHistory(cardKey);
              } else {
                setDropNodes(nodesOpen ? false : item.id);
              }
            }}
          >
            <span>
              {hasHistoryDisclosure ?
                t(historyOpen ? "HIDE_USAGE_HISTORY" : "SHOW_USAGE_HISTORY")
              :
                t(nodesOpen ? "HIDE_POOL_NODES" : "SHOW_POOL_NODES")
              }
            </span>
            <CardDisclosureChevron aria-hidden="true" $open={hasHistoryDisclosure ? historyOpen : nodesOpen} src={icons.Chevron} />
          </CardDisclosureToggle>
        : null}
      </TableCard>
    );
  };

  const selectAllState: "none" | "some" | "all" = allChecked ? "all" : selectedCount > 0 ? "some" : "none";
  const cardToolbarControls = isCardView ? (
    <CardToolbarControls>
      {canSelectAll ?
        <SelectAllButton
          type="button"
          role="checkbox"
          aria-checked={selectAllState === "all" ? true : selectAllState === "some" ? "mixed" : false}
          onClick={toggleAll}
        >
          <SelectAllBox aria-hidden="true" $state={selectAllState} />
          <span>{t("SELECT_ALL")}</span>
        </SelectAllButton>
      : null}
      {sortableColumns.length > 0 ?
        <ToolbarSelect>
          <Select
            data={sortOptions}
            selectedValue={ordering || ""}
            title=""
            renderInPortal
            customStyles={{ width: "100%" }}
            onChange={(value) => {
              setOrdering(value || undefined);
              setPage(1);
            }}
          />
        </ToolbarSelect>
      : null}
    </CardToolbarControls>
  ) : null;
  const showSelectionBar = Boolean(selectedCount > 0 && (selectionActions || (isCardView && canSelectAll)));

  return (
    <TableWrapper>
      {desktopCardViewEnabled || isCardView || searchable || toolbarActions || title ?
        <TableControlsPanel>
          <TableToolbar>
            {title ? <ToolbarTitle>{title}</ToolbarTitle> : null}
            {searchable ?
              <ToolbarSearch
                type="search"
                name="ocp-table-search"
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                value={searchValue}
                aria-label={searchPlaceholder || t("SEARCH")}
                placeholder={searchPlaceholder || t("SEARCH")}
                onChange={(event) => {
                  const modalOpen = document.querySelector('#modalContainer[data-open="true"]');
                  if (modalOpen || document.activeElement !== event.currentTarget) {
                    event.currentTarget.value = searchValue;
                    return;
                  }
                  setSearchValue(event.currentTarget.value);
                }}
              />
            : null}
            {toolbarActions ? <ToolbarActions>{toolbarActions}</ToolbarActions> : null}
            {cardToolbarControls}
            {title ? null : <ToolbarMeta role="status" aria-live="polite">{t("ITEM_COUNT", { count: totalCount })}</ToolbarMeta>}
            {desktopCardViewEnabled ?
              <ViewSwitcher role="group" aria-label={t("TABLE_VIEW")}>
                <ViewButton type="button" $active={view === "table"} onClick={() => changeView("table")}>
                  {t("TABLE_VIEW")}
                </ViewButton>
                <ViewButton type="button" $active={view === "cards"} onClick={() => changeView("cards")}>
                  {t("CARD_VIEW")}
                </ViewButton>
              </ViewSwitcher>
            : null}
          </TableToolbar>
          {showSelectionBar ?
            <SelectionBar role="status" aria-live="polite">
              <SelectionBarLabel>{t("SELECTED_OF_ITEMS", { count: selectedCount, total: totalCount })}</SelectionBarLabel>
              {selectionActions}
              <SelectionClearButton type="button" onClick={clearSelection}>{t("CLEAR_SELECTION")}</SelectionClearButton>
            </SelectionBar>
          : null}
        </TableControlsPanel>
      : null}

      {loading ? null : searchable && (search || hasActiveFilters) && rows.length === 0 ?
        <TableEmptyState>{t("NO_DATA_SEARCHED")}</TableEmptyState>
      : isCardView ?
        <CardsGrid>
          {rows.map((item, index) => (
            <React.Fragment key={rowKey(item, index, "card-group")}>
              {renderCard(item, index)}
              {dropNodes === item?.id && item?.nodes?.map((child: any, childIndex: number) => renderCard(child, childIndex, true))}
            </React.Fragment>
          ))}
        </CardsGrid>
      :
        <TableCustom>
          {!withoutHeader ?
            <TableColumnItem
              hover={hover}
              setHover={setHover}
              columns={resolvedColumns}
              rows={rows}
              hideCheckbox={hideCheckbox}
              isNotifications={isNotifications}
              settings={settings}
              limitedItems={limitedItems}
              hideOther={hideOther}
              allChecked={allChecked}
              setAllChecked={setAllChecked}
              checked={checked}
              setChecked={setChecked}
              canSelectAll={canSelectAll}
              toggleAll={toggleAll}
            />
          : null}
          <tbody>
            {rows.map((item, index) => (
              <React.Fragment key={rowKey(item, index, "table-group")}>
                {renderTableRow(item, index)}
                {dropNodes === item?.id && item?.nodes?.map((child: any, childIndex: number) => renderTableRow(child, childIndex, true))}
              </React.Fragment>
            ))}
          </tbody>
        </TableCustom>
      }

      {hideOther || !paginationData || paginationData?.count <= 10 ? null :
        <RowWrapper>
          <div style={{ display: "flex", alignItems: "center" }}>
            <BodyTitle style={{ marginRight: "7px" }}>{t("SHOW_ITEMS")}</BodyTitle>
            <div style={{ width: "46px" }}>
              <Select
                isSmall
                data={pageSizeOptions}
                selectedValue={+showItems || pageSizeOptions[0].value}
                title=""
                onChange={(count) => {
                  if (customSetPage) {
                    customSetPage(1);
                  } else {
                    setPage(1);
                  }
                  if (customSetPageSize) {
                    customSetPageSize(count);
                  } else {
                    setPageSize(count);
                  }
                }}
              />
            </div>
            <BodyTitle style={{ marginLeft: "7px" }}>{t("OF")} {paginationData?.count}</BodyTitle>
          </div>
          <Pages
            changePage={() => undefined}
            customSetPage={customSetPage}
            currentPage={+paginationData?.pageNo || +page || 1}
            totalCount={paginationData?.count || 10}
            pageSize={showItems || 10}
            siblingCount={1}
          />
        </RowWrapper>
      }
    </TableWrapper>
  );
};

export default Table;

type PreRowItemProps = {
  item: any;
  index: number;
  column: Column;
  first?: boolean;
  disableSettings?: boolean;
  layout?: "table" | "card";
  cardRole?: "primary" | "status" | "field" | "action" | "utility";
  fullWidth?: boolean;
  [key: string]: any;
};

const PreRowItem: FC<PreRowItemProps> = (props) => {
  const {
    hasDroppedRow,
    item,
    index,
    column,
    setHover,
    hover,
    hideCheckbox,
    isNotifications,
    settings,
    limitedItems,
    allChecked,
    checked,
    setChecked,
    dropNodes,
    setDropNodes,
    disableSettings = false,
    filterSettings,
    first = false,
    layout = "table",
    cardRole,
    fullWidth,
  } = props;
  const [options, setOptions] = useState(false);
  const [drop, setDrop] = useState(false);

  return (
    <TableRowItem
      dropNodes={dropNodes}
      setDropNodes={setDropNodes}
      hasDroppedRow={hasDroppedRow}
      rowKey={item?.id || item?.name || item?.title}
      item={item[column.key]}
      itemToEdit={item}
      navigateTo={item.navigate}
      isRead={item.isRead}
      darkBack={index % 2 === 0}
      withBack={column.withBackground}
      testId={column.key}
      columnTitle={column.title}
      first={first}
      more={column.key === "props" && !disableSettings}
      eye={column.key === "eye"}
      generated={column.key === "generated"}
      setHover={setHover}
      index={index}
      hover={hover === index}
      hideCheckbox={hideCheckbox}
      isNotifications={isNotifications}
      settings={settings}
      limitedItems={limitedItems}
      allChecked={allChecked}
      checked={checked}
      setChecked={setChecked}
      filterSettings={filterSettings}
      options={options}
      drop={drop}
      setDrop={setDrop}
      setOptions={setOptions}
      layout={layout}
      cardRole={cardRole}
      fullWidth={fullWidth}
    />
  );
};
